import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FacilityProvider } from './contexts/FacilityContext';
import ProtectedRoute from './components/ProtectedRoute';
import Dashboard from './pages/Dashboard';
import FacilityHome from './pages/FacilityHome';
import Patients from './pages/Patients';
import PatientSummary from './pages/PatientSummary';
import Claims from './pages/Claims';
import ClaimDetails from './pages/ClaimDetails';
import Worklist from './pages/Worklist';
import Holds from './pages/Holds';
import SyncHealth from './pages/SyncHealth';
import GenerateClaim from './pages/GenerateClaim';
import Revenue from './pages/Revenue';
import AR from './pages/AR';
import PaymentsPage from './pages/Payments';
import CreditBalances from './pages/CreditBalances';
import Clearinghouse from './pages/Clearinghouse';
import AccountSettings from './pages/AccountSettings';
import MfaSettings from './pages/MfaSettings';
import BillingSettings from './pages/BillingSettings';
import CptCodeSettings from './pages/CptCodeSettings';
import Payers from './pages/Payers';
import Providers from './pages/Providers';
import Clients from './pages/Clients';
import ClientDetail from './pages/ClientDetail';
import ActiveUsers from './pages/ActiveUsers';
import DeletedUsers from './pages/DeletedUsers';
import AuditLogs from './pages/AuditLogs';
import MergePatients from './pages/MergePatients';
import PatientReviews from './pages/PatientReviews';
import PriorAuthRules from './pages/PriorAuthRules';
import Login from './pages/Login';
import VerifyEmail from './pages/VerifyEmail';
import ResetPassword from './pages/ResetPassword';
import MfaVerify from './pages/MfaVerify';
import MfaSetup from './pages/MfaSetup';
import NotFound from './pages/NotFound';
import Layout from './components/Layout';
import './App.css';

function AdminRoute({ children }) {
  const { user } = useAuth();
  if (user?.role !== 'admin') return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public auth routes */}
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/mfa-verify" element={<MfaVerify />} />
      <Route path="/mfa-setup" element={user ? <MfaSetup /> : <Navigate to="/login" replace />} />

      {/* Protected routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <Layout><Dashboard /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/facility/:id" element={
        <ProtectedRoute>
          <Layout><FacilityHome /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/patients" element={
        <ProtectedRoute>
          <Layout><Patients /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/patients/:id" element={
        <ProtectedRoute>
          <Layout><PatientSummary /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/claims" element={
        <ProtectedRoute>
          <Layout><Claims /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/claims/:id" element={
        <ProtectedRoute>
          <Layout><ClaimDetails /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/worklist" element={
        <ProtectedRoute>
          <Layout><Worklist /></Layout>
        </ProtectedRoute>
      } />
      {/* Staff, facility-FILTERED (plan Step 6, S6-c): working holds is routine billing work. */}
      <Route path="/holds" element={
        <ProtectedRoute>
          <Layout><Holds /></Layout>
        </ProtectedRoute>
      } />
      {/* Staff, facility-FILTERED, read-only (plan Step 15): lag, volume, stale intake, holds by reason. */}
      <Route path="/sync-health" element={
        <ProtectedRoute>
          <Layout><SyncHealth /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/worklist/generate" element={
        <ProtectedRoute>
          <Layout><GenerateClaim /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/revenue" element={
        <ProtectedRoute>
          <Layout><Revenue /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/revenue/ar" element={
        <ProtectedRoute>
          <Layout><AR /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/revenue/payments" element={
        <ProtectedRoute>
          <Layout><PaymentsPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/revenue/credit-balances" element={
        <ProtectedRoute>
          <Layout><CreditBalances /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/revenue/clearinghouse" element={
        <ProtectedRoute>
          <Layout><Clearinghouse /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={<Navigate to="/settings/account" replace />} />
      <Route path="/settings/account" element={
        <ProtectedRoute>
          <Layout><AccountSettings /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/settings/mfa" element={
        <ProtectedRoute>
          <Layout><MfaSettings /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/settings/billing" element={
        <ProtectedRoute>
          <Layout><BillingSettings /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/settings/cpt-codes" element={
        <ProtectedRoute>
          <Layout><CptCodeSettings /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/payers" element={
        <ProtectedRoute>
          <Layout><Payers /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/providers" element={
        <ProtectedRoute>
          <Layout><Providers /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/clients" element={
        <ProtectedRoute>
          <Layout><Clients /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/clients/:id" element={
        <ProtectedRoute>
          <Layout><ClientDetail /></Layout>
        </ProtectedRoute>
      } />

      {/* Admin routes */}
      <Route path="/admin" element={<Navigate to="/admin/users" replace />} />
      <Route path="/admin/users" element={
        <ProtectedRoute>
          <AdminRoute>
            <Layout><ActiveUsers /></Layout>
          </AdminRoute>
        </ProtectedRoute>
      } />
      <Route path="/admin/deleted-users" element={
        <ProtectedRoute>
          <AdminRoute>
            <Layout><DeletedUsers /></Layout>
          </AdminRoute>
        </ProtectedRoute>
      } />
      <Route path="/admin/merge-patients" element={
        <ProtectedRoute>
          <AdminRoute>
            <Layout><MergePatients /></Layout>
          </AdminRoute>
        </ProtectedRoute>
      } />
      <Route path="/admin/patient-reviews" element={
        <ProtectedRoute>
          <AdminRoute>
            <Layout><PatientReviews /></Layout>
          </AdminRoute>
        </ProtectedRoute>
      } />
      <Route path="/admin/prior-auth-rules" element={
        <ProtectedRoute>
          <AdminRoute>
            <Layout><PriorAuthRules /></Layout>
          </AdminRoute>
        </ProtectedRoute>
      } />
      <Route path="/admin/audit-logs" element={
        <ProtectedRoute>
          <AdminRoute>
            <Layout><AuditLogs /></Layout>
          </AdminRoute>
        </ProtectedRoute>
      } />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <FacilityProvider>
          <AppRoutes />
        </FacilityProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
